'use client';

import * as React from 'react';
import Image from 'next/image';
import NiceModal from '@ebay/nice-modal-react';
import { ChevronDownIcon, EyeIcon, ImageIcon, XIcon } from 'lucide-react';

import { Button } from '@workspace/ui/components/button';
import { Card, CardContent } from '@workspace/ui/components/card';
import { cn } from '@workspace/ui/lib/utils';

import { FullscreenImageModal } from '~/components/generator/shared/fullscreen-image-modal';
import { PortfolioImagesModal } from '~/components/generator/shared/portfolio-images-modal';
import type { ModelVariantsDto } from '~/types/dtos/model-variant-dto';
import { ModelVariantDto } from '~/types/dtos/model-variant-dto';

type ModelVariantCardProps = {
  item: ModelVariantsDto;
  onSelect: (modelVariant: ModelVariantsDto) => void;
  isSelected: boolean;
  disabled?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
};

export function ModelVariantCard({
  item: model,
  onSelect,
  isSelected,
  disabled,
  selectedIds = [],
  onSelectionChange
}: ModelVariantCardProps): React.JSX.Element {
  const [selectedVariantIndex, setSelectedVariantIndex] = React.useState(0);
  const [showVariants, setShowVariants] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Ensure we have a valid variant index
  const validVariantIndex =
    model.variants && model.variants.length > 0
      ? Math.min(selectedVariantIndex, model.variants.length - 1)
      : 0;

  const selectedVariant = model.variants?.[validVariantIndex];
  const defaultImageUrl = model.image.url; // Model's avatar image
  const variantImageUrl = selectedVariant?.image.url; // Selected variant's image

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowVariants(false);
      }
    };

    if (showVariants) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showVariants]);

  const handleVariantChange = (variantIndex: number) => {
    setSelectedVariantIndex(variantIndex);
    setShowVariants(false);

    if (isSelected && onSelectionChange) {
      const newVariant = model.variants?.[variantIndex];
      if (!newVariant) return;

      // Find the currently selected variant ID for this model
      const currentSelectedVariantId = model.variants?.find((variant) =>
        selectedIds.includes(variant.id)
      )?.id;

      if (currentSelectedVariantId) {
        // Remove the old variant and add the new one
        const updatedSelectedIds = selectedIds
          .filter((id) => id !== currentSelectedVariantId)
          .concat(newVariant.id);

        onSelectionChange(updatedSelectedIds);
      }
    }
  };

  const handleSelect = () => {
    const selectedVariantData = model.variants?.[validVariantIndex];
    if (!selectedVariantData) return;
    const modelWithSelectedVariant: ModelVariantsDto = {
      ...model,
      id: selectedVariantData.id, // Use variant ID for selection
      size: selectedVariantData.size,
      image: selectedVariantData.image
    };
    onSelect(modelWithSelectedVariant);
  };

  return (
    <Card
      className={cn(
        'group h-48 w-full p-0 transition-all md:h-64 overflow-hidden rounded-lg',
        isSelected && 'border border-2 border-primary',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'cursor-pointer hover:shadow-md'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={
        disabled
          ? undefined
          : () => {
              // Only select if not clicking on dropdown
              if (!showVariants) {
                handleSelect();
              }
            }
      }
    >
      <CardContent className="h-full p-0">
        <div className="relative h-full w-full overflow-hidden">
          {defaultImageUrl ? (
            <>
              {/* Background blur image */}
              {/* <Image
                src={defaultImageUrl}
                alt=""
                width={100}
                height={100}
                className="absolute inset-0 h-full w-full scale-110 object-cover blur-sm"
              /> */}
              {/* Default model avatar image */}
              <Image
                src={defaultImageUrl}
                alt={model.modelName}
                width={200}
                height={200}
                className={cn(
                  'absolute inset-0 h-full w-full object-contain transition-all duration-300 object-cover',
                  (isSelected || showVariants || isHovered) && variantImageUrl
                    ? 'opacity-0 scale-95'
                    : 'opacity-100 group-hover:scale-105'
                )}
              />

              {/* Variant image - shown on hover or selection with smooth transition */}
              {variantImageUrl && (
                <Image
                  key={`${variantImageUrl}-${validVariantIndex}`} // Key forces re-render for smooth transition
                  src={variantImageUrl}
                  alt={`${model.modelName} - ${selectedVariant?.size}`}
                  width={200}
                  height={200}
                  className={cn(
                    'absolute inset-0 h-full w-full object-contain transition-all duration-500 ease-in-out object-cover object-top origin-top',
                    isSelected || showVariants || isHovered
                      ? 'opacity-100 scale-100'
                      : 'opacity-0 scale-110'
                  )}
                />
              )}
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <span className="text-sm text-muted-foreground">No image</span>
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

          {/* Preview Button */}
          {model.portfolioImages && model.portfolioImages.length > 0 && (
            <div className="absolute top-2 right-2 z-50 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200">
              <Button
                type="button"
                variant="secondary"
                className="h-8 bg-black/20 border-black/30 text-white hover:bg-black/30 flex items-center gap-1.5"
                onClick={(e) => {
                  e.stopPropagation();
                  NiceModal.show(PortfolioImagesModal, {
                    modelName: model.modelName,
                    portfolioImages: model.portfolioImages
                  });
                }}
                title="View portfolio images"
              >
                <EyeIcon className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Variant Selection Pill */}
          {model.variants && model.variants.length > 0 && (
            <div className="absolute bottom-2 right-2 z-10">
              <div
                className="relative"
                ref={dropdownRef}
              >
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-8 px-2 text-xs bg-white/20 border-white/30 text-white hover:bg-white/30"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowVariants(!showVariants);
                  }}
                >
                  {selectedVariant?.size || model.size}
                  <ChevronDownIcon className="ml-1 h-3 w-3" />
                </Button>

                {showVariants && model.variants && (
                  <div className="absolute bottom-full right-0 mb-2 min-w-[80px] rounded-md border bg-popover p-1 shadow-md gap-1">
                    {model.variants.map((variant, index) => (
                      <button
                        key={variant.id}
                        type="button"
                        className={cn(
                          'relative w-full select-none items-center rounded-sm px-2 py-1.5 text-left text-xs outline-none transition-colors mb-1 last:mb-0',
                          'focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                          index === validVariantIndex
                            ? 'bg-accent text-accent-foreground'
                            : 'hover:bg-accent hover:text-accent-foreground'
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVariantChange(index);
                        }}
                      >
                        {variant.size}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <div className="space-y-2">
              <div>
                <h4 className="line-clamp-1 text-sm font-medium">
                  {model.modelName}
                </h4>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type SelectedModelVariantCardProps = {
  item: ModelVariantDto;
  onRemove?: (modelVariantId: string) => void;
};

export function SelectedModelVariantCard({
  item: modelVariant,
  onRemove
}: SelectedModelVariantCardProps): React.JSX.Element {
  const imageUrl = modelVariant.image?.url;
  return (
    <Card
      className="group relative h-12 w-12 cursor-pointer p-0 transition-all hover:shadow-md md:h-18 md:w-18"
      onClick={() => {
        if (imageUrl) {
          NiceModal.show(FullscreenImageModal, {
            images: [
              {
                url: imageUrl,
                title: modelVariant.model.name,
                filename: `model-${new Date().toISOString().split('T')[0]}.jpg`
              }
            ],
            currentIndex: 0
          });
        }
      }}
    >
      <CardContent className="h-full p-0">
        <div className="relative h-full w-full overflow-hidden rounded-lg">
          {imageUrl ? (
            <>
              <Image
                src={imageUrl}
                alt={modelVariant.model.name}
                width={72}
                height={72}
                className="absolute inset-0 h-full w-full object-cover object-top transition-transform group-hover:scale-105"
              />
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <span className="text-sm text-muted-foreground">
                <ImageIcon className="h-4 w-4" />
              </span>
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

          {/* Size indicator */}
          <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1 py-0.5 text-center">
            <span className="text-xs text-white">{modelVariant.size}</span>
          </div>
        </div>
      </CardContent>
      {onRemove && (
        <div className="absolute right-0 top-0 opacity-0 transition-opacity group-hover:opacity-100 sm:right-1 sm:top-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(modelVariant.id);
            }}
            className="h-12 w-12 bg-background/80 p-0 hover:bg-background hover:text-destructive sm:h-6 sm:w-6"
            title="Remove model variant"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      )}
    </Card>
  );
}
